/**
 * ToolBlock Component (Parametric Animation Refactor)
 *
 * Visual representation of a tool on the timeline.
 *
 * Features:
 * - Displays tool name and target rotation
 * - Width calculated as percentage of timeline duration
 * - Color-coded background by body part
 * - Rounded corners with subtle shadow
 * - Hover state with Framer Motion transitions
 * - Click to select
 * - Draggable for repositioning
 * - Tooltip showing target rotation and easing
 * - Minimum visual width (40px) for short tools (Task Group 2)
 *   - Solid portion represents actual duration
 *   - Transparent/outline portion represents visual padding
 * - STOP control tool visual redesign (Task Group 3)
 *   - Colorful left border "wall" using control color (#ef4444)
 *   - Gray body (#4a4a4a) with dot pattern
 *   - Visually distinct from movement tools
 * - Duration resize via edge dragging (Task Group 4)
 *   - Right edge: changes duration only
 *   - Left edge: changes startTime AND duration (endTime fixed)
 *   - Snap-to-grid at 0.1s intervals
 *   - Minimum duration enforcement (0.1s)
 * - Hover parameter popup for inline editing (Task Group 5)
 *   - Duration input (all tools)
 *   - Target Rotation input (movement tools only)
 *   - Easing dropdown (movement tools only)
 */

import {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
  memo,
} from 'react';
import { useDrag } from 'react-dnd';
import { motion } from 'framer-motion';
import * as Tooltip from '@radix-ui/react-tooltip';
import type { BodyPart } from '../../stores/timelineStore';
import type { Tool } from '../../stores/timelineStore';
import { useTimelineStore } from '../../stores/timelineStore';
import { usePuppetStore } from '../../stores/puppetStore';
import { useUiStore } from '../../stores/uiStore';
import { DND_ITEM_TYPES } from '../sidebar/dndTypes';
import {
  getBodyPartColor as getColor,
  BODY_PART_COLORS,
} from '../../utils/bodyPartColors';
import {
  useResizeHandles,
  EDGE_THRESHOLD,
  type ResizeEdge,
} from './hooks/useResizeHandles';
import { useTimelineBlock } from './hooks/useTimelineBlock';
import { ToolBlockPopover } from './ToolBlockPopover';
import { formatDurationDisplay } from './utils/formatDuration';
import { isResetPositionTool } from '../../utils/resetPositionTool';

/**
 * Minimum visual width in pixels for tool blocks (Task Group 2)
 * Short duration tools will be rendered at this minimum width
 * with transparent padding to indicate the visual extension
 */
export const MIN_VISUAL_WIDTH = 40;

/**
 * STOP tool styling constants (Task Group 3)
 */
const STOP_TOOL_STYLES = {
  /** Width of the colorful left border "wall" in pixels */
  WALL_WIDTH: 5,
  /** Gray background color for STOP tool body */
  BODY_BACKGROUND: '#4a4a4a',
  /** Dot pattern for STOP tool body - subtle white dots */
  DOT_PATTERN:
    'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
  /** Dot pattern size for spacing */
  DOT_PATTERN_SIZE: '8px 8px',
} as const;

interface ToolBlockProps {
  /**
   * Tool data from timeline store
   */
  tool: Tool;

  /**
   * Pixels per second for positioning and sizing
   * Default: 100px/s
   */
  pixelsPerSecond?: number;

  /**
   * Optional override for selection state (used in tests).
   * If undefined, the component subscribes to uiStore for selection state.
   */
  isSelected?: boolean;

  /**
   * Callback when tool is updated (for resize and popover operations)
   * Task Groups 4 & 5
   */
  onUpdate?: (toolId: string, updates: Partial<Tool>) => void;
}

/**
 * Info about a selected tool for multi-selection drag
 */
interface SelectedToolInfo {
  id: string;
  trackNumber: number;
  startTime: number;
}

/**
 * Data structure for dragging timeline tools
 */
export interface TimelineToolDragItem {
  type: typeof DND_ITEM_TYPES.TIMELINE_TOOL;
  toolId: string;
  originalTrackNumber: number;
  originalStartTime: number;
  /**
   * Offset in pixels from the left edge of the tool where the user grabbed it.
   * Used to preserve the grab point when dropping - the tool should land
   * so the cursor remains at the same relative position within the tool.
   */
  grabOffsetX: number;
  /**
   * When dragging a tool that's part of a multi-selection,
   * this contains info about all selected tools (including the dragged one)
   * for calculating relative positions during drop
   */
  selectedTools?: SelectedToolInfo[];
}

/**
 * Get background color for body part
 */
function getBodyPartColor(bodyPart: BodyPart): string {
  return getColor(bodyPart);
}

/**
 * Get hover color for body part
 * Lightens the base color by 10%
 */
function getBodyPartHoverColor(bodyPart: BodyPart): string {
  const baseColor = getColor(bodyPart);
  // Convert hex to RGB and lighten
  const hex = baseColor.replace('#', '');
  const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + 25);
  const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + 25);
  const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + 25);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Convert hex color to rgba with specified opacity
 */
function hexToRgba(hex: string, opacity: number): string {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Get readable display name for tool based on toolId
 * Falls back to tool ID if not found
 */
function getToolDisplayName(toolId: string): string {
  // Extract name from tool ID (e.g., "prebuilt-head" -> "Head")
  if (toolId.startsWith('prebuilt-')) {
    const parts = toolId.replace('prebuilt-', '').split('-');
    // Capitalize each part
    return parts
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // For custom tools, display as-is with capitalization
  return toolId
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get display label for body part
 */
function getBodyPartLabel(bodyPart: BodyPart): string {
  const labels: Record<BodyPart, string> = {
    head: 'Head',
    body: 'Body',
    leftArm: 'Left Arm',
    rightArm: 'Right Arm',
    leftLeg: 'Left Leg',
    rightLeg: 'Right Leg',
    control: 'Control',
  };
  return labels[bodyPart];
}

/**
 * Get display label for easing function
 */
function getEasingLabel(easing: string): string {
  const labels: Record<string, string> = {
    linear: 'Linear',
    'ease-in': 'Ease In',
    'ease-out': 'Ease Out',
    'ease-in-out': 'Ease In-Out',
  };
  return labels[easing] ?? easing;
}

/**
 * Check if a tool is a STOP control tool (Task Group 3)
 * STOP tools get special visual styling with wall border and gray dotted body
 */
function isStopControlTool(tool: Tool): boolean {
  // Check if toolId contains "stop" (case-insensitive)
  const toolIdLower = tool.toolId.toLowerCase();
  return toolIdLower.includes('stop') && tool.bodyPart === 'control';
}

/**
 * Check if a tool is a Reset Position control tool
 * Reset Position tools get STOP-style visual styling (wall + dotted body)
 */
function isResetPositionControlTool(tool: Tool): boolean {
  return isResetPositionTool(tool.toolId) && tool.bodyPart === 'control';
}

/**
 * Get display label for Reset Position tool's target position
 */
function getResetPositionLabel(
  targetPosition: 'start' | 'dock' | undefined
): string {
  return targetPosition === 'dock' ? 'Reset: Dock' : 'Reset: Start';
}

/**
 * Generate SVG path for easing curve visualization
 * Returns a path that represents the easing function as a line
 * Returns null for linear (default) since it needs no visualization
 * Y-axis is inverted (0 at top, 1 at bottom) for visual representation
 */
function getEasingCurvePath(easing: string): string | null {
  // Path coordinates: M startX,startY followed by curve/line commands
  // Width is 100 (will be scaled via viewBox), height represents progress (0-1 mapped to viewBox)
  switch (easing) {
    case 'ease-in':
      // Quadratic ease-in: starts slow, ends fast
      // Control point at bottom-left area
      return 'M 0,100 Q 60,95 100,0';
    case 'ease-out':
      // Quadratic ease-out: starts fast, ends slow
      // Control point at top-right area
      return 'M 0,100 Q 40,5 100,0';
    case 'ease-in-out':
      // Cubic ease-in-out: slow start, fast middle, slow end
      return 'M 0,100 C 30,95 70,5 100,0';
    case 'linear':
    default:
      // Linear is the default - no visualization needed
      return null;
  }
}

export const ToolBlock = memo(function ToolBlock({
  tool,
  pixelsPerSecond = 100,
  isSelected: isSelectedProp,
  onUpdate,
}: ToolBlockProps) {
  // Ref to track the element for calculating grab offset and edge detection
  const toolBlockRef = useRef<HTMLDivElement>(null);

  // Unified selection handling via shared hook
  const { isSelected, handleSelectionClick } = useTimelineBlock({
    id: tool.id,
    isSelectedOverride: isSelectedProp,
  });

  // Check if rubber-band selection is active - disable hover effects when true
  const isRubberBandSelecting = useUiStore(
    (state) => state.isRubberBandSelecting
  );

  // Track which edge zone the mouse is in for cursor changes (Task Group 4)
  const [hoveredEdge, setHoveredEdge] = useState<ResizeEdge>(null);

  // Popover open state - opens on click (Task Group 5)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverCursorPos, setPopoverCursorPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const lastClickHadModifierRef = useRef(false);

  // Initialize resize handles hook (Task Group 4)
  const resize = useResizeHandles({
    tool,
    pixelsPerSecond,
    onUpdate: useCallback(
      (updates: { startTime: number; duration: number }) => {
        if (onUpdate) {
          onUpdate(tool.id, updates);
        }
      },
      [onUpdate, tool.id]
    ),
  });

  // Ref to always have latest resize handlers (avoids stale closures)
  const resizeHandlersRef = useRef(resize);

  // Update ref after each render (useLayoutEffect runs before other effects)
  useLayoutEffect(() => {
    resizeHandlersRef.current = resize;
  });

  // Set up global mouse event listeners for resize (Task Group 4)
  // Using ref pattern to avoid effect re-running on every render
  useEffect(() => {
    if (!resize.isResizing) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      resizeHandlersRef.current.handleMouseMove(e.clientX);
    };

    const handleGlobalMouseUp = () => {
      resizeHandlersRef.current.handleMouseUp();
      // Clear hoveredEdge after resize completes to re-enable drag-and-drop
      // Without this, the edge state persists and blocks dragging
      setHoveredEdge(null);
    };

    // Add global listeners during resize
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [resize.isResizing]);

  // Set up drag behavior for timeline tool blocks
  // Disable drag when resizing (Task Group 4)
  const [{ isDragging }, drag] = useDrag<
    TimelineToolDragItem,
    unknown,
    { isDragging: boolean }
  >(
    () => ({
      type: DND_ITEM_TYPES.TIMELINE_TOOL,
      item: (monitor) => {
        // Calculate grab offset: where within the tool block the user clicked
        const clientOffset = monitor.getClientOffset();
        const toolElement = toolBlockRef.current;

        let grabOffsetX = 0;
        if (clientOffset && toolElement) {
          const toolRect = toolElement.getBoundingClientRect();
          grabOffsetX = clientOffset.x - toolRect.left;
        }

        // Check if this tool is part of a multi-selection
        const selectedToolIds = useUiStore.getState().selectedToolIds;
        const allTools = useTimelineStore.getState().tools;
        const isPartOfSelection = selectedToolIds.has(tool.id);

        // If dragging a selected tool, include all selected tools' info
        let selectedTools: SelectedToolInfo[] | undefined;
        if (isPartOfSelection && selectedToolIds.size > 1) {
          selectedTools = allTools
            .filter((t) => selectedToolIds.has(t.id))
            .map((t) => ({
              id: t.id,
              trackNumber: t.trackNumber,
              startTime: t.startTime,
            }));
        }

        return {
          type: DND_ITEM_TYPES.TIMELINE_TOOL,
          toolId: tool.id,
          originalTrackNumber: tool.trackNumber,
          originalStartTime: tool.startTime,
          grabOffsetX,
          selectedTools,
        };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: () => !resize.isResizing && !hoveredEdge, // Disable drag when on edge or resizing
    }),
    [tool.id, tool.trackNumber, tool.startTime, resize.isResizing, hoveredEdge]
  );

  /**
   * Calculate visual dimensions with minimum width enforcement (Task Group 2)
   * During resize, use preview dimensions for responsive feedback (Task Group 4)
   */
  const {
    actualWidth,
    displayWidth,
    paddingWidth,
    hasPadding,
    effectiveStartTime,
    effectiveDuration,
  } = useMemo(() => {
    // Use preview dimensions during resize, otherwise use tool values
    const startTime = resize.previewDimensions?.startTime ?? tool.startTime;
    const duration = resize.previewDimensions?.duration ?? tool.duration;

    const actual = duration * pixelsPerSecond;
    const display = Math.max(MIN_VISUAL_WIDTH, actual);
    const padding = display - actual;
    return {
      actualWidth: actual,
      displayWidth: display,
      paddingWidth: padding,
      hasPadding: padding > 0,
      effectiveStartTime: startTime,
      effectiveDuration: duration,
    };
  }, [
    tool.duration,
    tool.startTime,
    pixelsPerSecond,
    resize.previewDimensions,
  ]);

  // Calculate position in pixels (use preview during resize)
  const leftPosition = effectiveStartTime * pixelsPerSecond;

  // Get colors
  const backgroundColor = getBodyPartColor(tool.bodyPart);
  const hoverColor = getBodyPartHoverColor(tool.bodyPart);

  // Get display name
  const displayName = getToolDisplayName(tool.toolId);

  // Check if this is a control tool
  const isControlTool = tool.bodyPart === 'control';

  // Multi-puppet support: check if this tool belongs to the active puppet
  const activePuppetId = usePuppetStore((state) => state.activePuppetId);
  const getPuppetNumber = usePuppetStore((state) => state.getPuppetNumber);

  // Tool is from inactive puppet if it has a puppetId and it's not the active one
  const isFromInactivePuppet =
    tool.puppetId != null && tool.puppetId !== activePuppetId;

  // Get puppet number for badge display (only show if multiple puppets exist or tool is from different puppet)
  const puppetNumber = tool.puppetId
    ? getPuppetNumber(tool.puppetId)
    : undefined;
  const puppetsCount = usePuppetStore((state) => state.puppets.length);
  const showPuppetBadge = puppetsCount > 1 && puppetNumber != null;

  // Check if this is specifically a STOP tool (Task Group 3)
  const isStopTool = isStopControlTool(tool);

  // Check if this is a Reset Position tool (uses STOP-style visuals)
  const isResetTool = isResetPositionControlTool(tool);

  // Combined check for STOP-style rendering (STOP or Reset Position)
  const useStopStyleRendering = isStopTool || isResetTool;

  /**
   * Handle popover update - wraps onUpdate to include tool ID (Task Group 5)
   */
  const handlePopoverUpdate = useCallback(
    (updates: Partial<Tool>) => {
      if (onUpdate) {
        onUpdate(tool.id, updates);
      }
    },
    [onUpdate, tool.id]
  );

  /**
   * Handle mouse enter (Task Group 4)
   */
  const handleMouseEnter = useCallback(() => {
    // Mouse enter is now only used for edge detection visual feedback
  }, []);

  /**
   * Handle mouse move for edge detection and cursor changes (Task Group 4)
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Disable edge detection during rubber-band selection or resize/drag
      if (resize.isResizing || isDragging || isRubberBandSelecting) return;

      const rect = toolBlockRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Calculate local X position within the tool block
      const localX = e.clientX - rect.left;
      const edge = resize.getEdgeZone(localX, displayWidth);
      setHoveredEdge(edge);
    },
    [resize, displayWidth, isDragging, isRubberBandSelecting]
  );

  /**
   * Handle mouse leave to reset edge detection (Task Group 4)
   */
  const handleMouseLeave = useCallback(() => {
    if (!resize.isResizing) {
      setHoveredEdge(null);
    }
  }, [resize.isResizing]);

  /**
   * Handle popover open change (Task Group 5)
   */
  const handlePopoverOpenChange = useCallback((open: boolean) => {
    if (open && lastClickHadModifierRef.current) return;
    setIsPopoverOpen(open);
  }, []);

  /**
   * Handle mouse down for resize initiation (Task Group 4)
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Close popover when starting any interaction
      setIsPopoverOpen(false);

      // If on an edge, start resize instead of allowing drag
      if (hoveredEdge) {
        e.preventDefault();
        e.stopPropagation();
        resize.handleMouseDown(hoveredEdge, e.clientX);
      }
    },
    [hoveredEdge, resize]
  );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (resize.isResizing || isDragging) return;

    const hasModifier = e.shiftKey || e.ctrlKey || e.metaKey || e.altKey;
    lastClickHadModifierRef.current = hasModifier;

    handleSelectionClick(e);

    if (!hoveredEdge && !hasModifier) {
      setPopoverCursorPos({ x: e.clientX, y: e.clientY });
      setIsPopoverOpen(true);
    }
  };

  // Combine refs: toolBlockRef for measuring grab offset, drag for DnD
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      toolBlockRef.current = node;
      // Only connect drag if not on an edge
      if (!hoveredEdge) {
        drag(node);
      }
    },
    [drag, hoveredEdge]
  );

  /**
   * Compute effective hovered edge - null when rubber-band selecting
   * This replaces the previous useEffect that called setState, which
   * caused cascading renders. Using a derived value is more efficient.
   */
  const effectiveHoveredEdge = isRubberBandSelecting ? null : hoveredEdge;

  /**
   * Determine cursor based on state (Task Group 4)
   * - ew-resize: when hovering over edges or during resize
   * - move: default draggable cursor
   * - grabbing: during drag (handled by DnD)
   */
  const getCursor = useCallback(() => {
    // effectiveHoveredEdge is already null when rubber-band selecting
    if (resize.isResizing || effectiveHoveredEdge) {
      return 'ew-resize';
    }
    if (isRubberBandSelecting) {
      return 'default';
    }
    return 'move';
  }, [resize.isResizing, effectiveHoveredEdge, isRubberBandSelecting]);

  /**
   * Render STOP tool with special styling (Task Group 3)
   * - Colorful left border "wall"
   * - Gray body with dot pattern
   */
  const renderStopToolContent = () => {
    const wallWidth = STOP_TOOL_STYLES.WALL_WIDTH;
    const bodyWidth = actualWidth - wallWidth;
    const controlColor = BODY_PART_COLORS.control;

    return (
      <>
        {/* Colorful left border "wall" (Task 3.3) */}
        <div
          className="absolute top-0 left-0 h-full pointer-events-none"
          style={{
            width: `${wallWidth}px`,
            backgroundColor: controlColor,
            borderRadius: hasPadding
              ? 'var(--radius-md) 0 0 var(--radius-md)'
              : 'var(--radius-md) 0 0 var(--radius-md)',
          }}
          data-testid={`tool-block-${tool.id}-wall`}
        />

        {/* Gray body with dot pattern (Task 3.4) */}
        <div
          className="absolute top-0 h-full pointer-events-none"
          style={{
            left: `${wallWidth}px`,
            width: `${Math.max(0, bodyWidth)}px`,
            backgroundColor: STOP_TOOL_STYLES.BODY_BACKGROUND,
            backgroundImage: STOP_TOOL_STYLES.DOT_PATTERN,
            backgroundSize: STOP_TOOL_STYLES.DOT_PATTERN_SIZE,
            borderRadius: hasPadding
              ? '0'
              : '0 var(--radius-md) var(--radius-md) 0',
          }}
          data-testid={`tool-block-${tool.id}-stop-body`}
        >
          {/* Tool content */}
          <div className="relative h-full px-2 py-1 flex items-center overflow-hidden">
            {/* Tool label (STOP or Reset Position) */}
            <span
              className="text-xs font-medium truncate whitespace-nowrap"
              style={{
                color: 'rgba(255, 255, 255, 0.95)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
              }}
            >
              {isResetTool
                ? getResetPositionLabel(tool.targetPosition)
                : displayName}
            </span>
          </div>
        </div>
      </>
    );
  };

  // Get easing curve path for visualization
  const easingCurvePath = useMemo(
    () => getEasingCurvePath(tool.easing),
    [tool.easing]
  );

  /**
   * Render standard tool content (non-STOP tools)
   */
  const renderStandardToolContent = () => (
    <motion.div
      className="absolute top-0 left-0 h-full pointer-events-none"
      style={{
        width: `${actualWidth}px`,
        backgroundColor,
        borderRadius: hasPadding
          ? 'var(--radius-md) 0 0 var(--radius-md)'
          : 'var(--radius-md)',
      }}
      whileHover={
        !resize.isResizing
          ? {
              backgroundColor: hoverColor,
              transition: { duration: 0.15 },
            }
          : undefined
      }
    >
      {/* Easing curve visualization - thin line showing easing function */}
      {/* Only shown for non-linear easing (linear is the default, no curve needed) */}
      {!isControlTool && easingCurvePath && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            borderRadius: hasPadding
              ? 'var(--radius-md) 0 0 var(--radius-md)'
              : 'var(--radius-md)',
          }}
          data-testid={`tool-block-${tool.id}-easing-curve`}
        >
          <path
            d={easingCurvePath}
            fill="none"
            stroke="rgba(255, 255, 255, 0.5)"
            strokeWidth="3"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}

      {/* Tool content */}
      <div className="relative h-full px-2 py-1 flex items-center overflow-hidden">
        {/* Tool name and rotation */}
        <span
          className="text-xs font-medium truncate whitespace-nowrap"
          style={{
            color: 'rgba(255, 255, 255, 0.95)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
          }}
        >
          {displayName}
          {!isControlTool && (
            <span className="ml-1 opacity-70">
              {tool.targetRotation >= 0 ? '+' : ''}
              {tool.targetRotation}deg
            </span>
          )}
        </span>

        {/* Duration indicator (visual bar at bottom) */}
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5 opacity-40"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          }}
        />
      </div>
    </motion.div>
  );

  /**
   * Render padding portion for short tools
   * Handles both STOP and standard tools
   */
  const renderPaddingPortion = () => {
    if (!hasPadding) return null;

    // For STOP tools, padding uses gray color
    // For standard tools, padding uses body part color
    const paddingBgColor = isStopTool
      ? hexToRgba(STOP_TOOL_STYLES.BODY_BACKGROUND, 0.35)
      : hexToRgba(backgroundColor, 0.35);
    const paddingBorderColor = isStopTool
      ? hexToRgba(STOP_TOOL_STYLES.BODY_BACKGROUND, 0.6)
      : hexToRgba(backgroundColor, 0.6);

    return (
      <div
        className="absolute top-0 h-full pointer-events-none"
        style={{
          left: `${actualWidth}px`,
          width: `${paddingWidth}px`,
          backgroundColor: paddingBgColor,
          borderRadius: '0 var(--radius-md) var(--radius-md) 0',
          borderRight: `2px dashed ${paddingBorderColor}`,
          borderTop: `1px dashed ${paddingBorderColor}`,
          borderBottom: `1px dashed ${paddingBorderColor}`,
        }}
        data-testid={`tool-block-${tool.id}-padding`}
      />
    );
  };

  /**
   * Render puppet number badge (Multi-puppet support)
   * Shows a small numbered badge in the top-right corner when multiple puppets exist
   */
  const renderPuppetBadge = () => {
    if (!showPuppetBadge) return null;

    return (
      <div
        className="absolute top-0.5 right-0.5 flex items-center justify-center pointer-events-none"
        style={{
          width: '16px',
          height: '16px',
          backgroundColor: isFromInactivePuppet
            ? 'rgba(100, 100, 100, 0.9)'
            : 'rgba(59, 130, 246, 0.9)',
          borderRadius: '50%',
          fontSize: '10px',
          fontWeight: 600,
          color: 'white',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
          zIndex: 15,
        }}
        data-testid={`tool-block-${tool.id}-puppet-badge`}
        title={`Puppet ${puppetNumber}`}
      >
        {puppetNumber}
      </div>
    );
  };

  /**
   * Render resize edge handles (Task Group 4)
   * Visual indicators for resize zones - highlight on hover and during resize
   */
  const renderResizeHandles = () => {
    // Show highlight on hover or during active resize
    const leftHighlight =
      effectiveHoveredEdge === 'left' ||
      (resize.isResizing && resize.resizeEdge === 'left');
    const rightHighlight =
      effectiveHoveredEdge === 'right' ||
      (resize.isResizing && resize.resizeEdge === 'right');

    return (
      <>
        {/* Left resize handle zone */}
        <div
          className="absolute top-0 left-0 h-full pointer-events-none"
          style={{
            width: `${EDGE_THRESHOLD}px`,
            // Highlight on hover or during resize
            backgroundColor: leftHighlight
              ? 'rgba(255, 255, 255, 0.25)'
              : 'transparent',
            borderLeft: leftHighlight
              ? '2px solid rgba(255, 255, 255, 0.5)'
              : 'none',
            borderRadius: 'var(--radius-md) 0 0 var(--radius-md)',
            transition: 'background-color 0.1s, border 0.1s',
          }}
          data-testid={`tool-block-${tool.id}-resize-left`}
        />

        {/* Right resize handle zone */}
        <div
          className="absolute top-0 right-0 h-full pointer-events-none"
          style={{
            width: `${EDGE_THRESHOLD}px`,
            // Highlight on hover or during resize
            backgroundColor: rightHighlight
              ? 'rgba(255, 255, 255, 0.25)'
              : 'transparent',
            borderRight: rightHighlight
              ? '2px solid rgba(255, 255, 255, 0.5)'
              : 'none',
            borderRadius: '0 var(--radius-md) var(--radius-md) 0',
            transition: 'background-color 0.1s, border 0.1s',
          }}
          data-testid={`tool-block-${tool.id}-resize-right`}
        />
      </>
    );
  };

  // The main tool block element
  const toolBlockElement = (
    <motion.div
      ref={setRefs}
      className="absolute top-0 h-full select-none"
      style={{
        left: `${leftPosition}px`,
        width: `${displayWidth}px`,
        borderRadius: 'var(--radius-md)',
        boxShadow: isSelected
          ? `0 0 0 2px var(--color-focus), var(--shadow-md)`
          : resize.isResizing
            ? 'var(--shadow-md)'
            : 'var(--shadow-sm)',
        border: isSelected
          ? '2px solid var(--color-focus)'
          : '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: isSelected || resize.isResizing ? 10 : 5,
        // Dim tools from inactive puppets
        opacity: isDragging ? 0.7 : isFromInactivePuppet ? 0.5 : 1,
        cursor: getCursor(),
        // Visual feedback during resize (Task 4.6)
        outline: resize.isResizing ? '2px solid var(--color-focus)' : undefined,
      }}
      initial={false}
      whileHover={
        // Disable hover effects during resize or rubber-band selection
        !resize.isResizing && !isRubberBandSelecting
          ? {
              scale: isDragging ? 1 : 1.01,
              boxShadow: isSelected
                ? `0 0 0 2px var(--color-focus), var(--shadow-md)`
                : 'var(--shadow-md)',
              transition: { duration: 0.15 },
            }
          : undefined
      }
      whileTap={
        // Disable tap effects during resize or rubber-band selection
        !resize.isResizing && !isRubberBandSelecting
          ? {
              scale: 0.98,
              transition: { duration: 0.1 },
            }
          : undefined
      }
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      data-testid={`tool-block-${tool.id}`}
      data-tool-id={tool.id}
      data-body-part={tool.bodyPart}
      data-puppet-id={tool.puppetId}
      data-inactive-puppet={isFromInactivePuppet ? 'true' : undefined}
      data-resizing={resize.isResizing ? 'true' : undefined}
    >
      {/* Render tool content based on type */}
      {useStopStyleRendering
        ? renderStopToolContent()
        : renderStandardToolContent()}

      {/* Transparent padding portion for short tools (Task Group 2) */}
      {renderPaddingPortion()}

      {/* Resize handles (Task Group 4) */}
      {renderResizeHandles()}

      {/* Puppet number badge (Multi-puppet support) */}
      {renderPuppetBadge()}
    </motion.div>
  );

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        {/* Wrap with popover for inline parameter editing (Task Group 5) */}
        <ToolBlockPopover
          tool={tool}
          onUpdate={handlePopoverUpdate}
          isOpen={isPopoverOpen}
          onOpenChange={handlePopoverOpenChange}
          disabled={resize.isResizing || isDragging}
          cursorPosition={popoverCursorPos}
        >
          {toolBlockElement}
        </ToolBlockPopover>
      </Tooltip.Trigger>

      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          sideOffset={5}
          className="bg-dark-bg border panel-border rounded-lg px-3 py-2 text-sm text-dark-text shadow-lg z-50 max-w-xs"
          style={{ boxShadow: 'var(--shadow-lg)' }}
        >
          <div className="space-y-1">
            <div className="font-semibold">{displayName}</div>
            <div className="text-xs text-dark-text-muted space-y-0.5">
              <div>Body Part: {getBodyPartLabel(tool.bodyPart)}</div>
              {/* Show preview values during resize, otherwise actual (Task Group 4) */}
              <div>Start: {formatDurationDisplay(effectiveStartTime)}</div>
              {/* Always show actual duration, not padded width (Task 2.5) */}
              <div>Duration: {formatDurationDisplay(effectiveDuration)}</div>
              {!isControlTool && (
                <>
                  <div>Target Rotation: {tool.targetRotation}deg</div>
                  <div>Easing: {getEasingLabel(tool.easing)}</div>
                </>
              )}
            </div>
          </div>
          <Tooltip.Arrow className="fill-dark-bg" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
});
